import { Button, Paper } from "@mui/material"
import { Stack } from "@mui/system"
import { MODULE_TEMPLATE_LANGUAGES } from "pages/student/activity/common/constants"
import React, { useEffect, useMemo, useState } from "react"
import { styles } from "./styles"
import { getKeyBoardTemplate } from "./templates"
import PropsTypes from "prop-types"
import "./theme/styles.css"

/**
 * Ex. [{ keys: ["a","b", ... ], className: "key-theme-a" },]
 * @param {*} keyTheme
 * @param {*} letter
 * @returns
 */

const getKeyThemeClass = (keyTheme, letter) => {
  const keyThemeClass = keyTheme.find((keyTheme) =>
    keyTheme.keys.includes(letter)
  )
  return keyThemeClass ? keyThemeClass.className : ""
}

/**
 * Ex.
 * @param {*} param0
 * @returns
 */
export default function VirtualKeyboard({
  paperRef,
  onKeyPress = () => console.log("onKeyPress", "attach method"),
  templateName,
  templateId,
  keyTheme = [],
  keyDisabled = [],
  disabled = false,
  fullKeyboard = true,
  keysBefore = [],
  keysAfter = [],
  keyStyle = null,
  listenKeys = true,
  useOneLine = false,
  useBackspace = false,
  useSpace = false,
  useNumPad = false,
  useEnter = false,
  numericPadActive = false,
  customTemplate = [],
  parentRef,
  onTabKeyPress = () => {},
  ...props
}) {
  const [template, setTemplate] = useState("french")

  useEffect(() => {
    try {
      if (templateId > 0) {
        // get template name according to templateId
        const templateName = Object.keys(MODULE_TEMPLATE_LANGUAGES).find(
          (key) => MODULE_TEMPLATE_LANGUAGES[key] === templateId
        )
        if (templateName) {
          setTemplate(templateName?.toLowerCase())
        } else {
          // set english as default
          setTemplate("english")
        }
      } else {
        setTemplate(templateName)
      }
    } catch (error) {
      console.error("template language not define")
    }
  }, [])

  const keyboardTemplate = useMemo(() => {
    if (customTemplate?.length > 0) {
      return customTemplate
    }
    return getKeyBoardTemplate(template, fullKeyboard, numericPadActive)
  }, [template, fullKeyboard, numericPadActive, customTemplate])

  const handleUserKeyPress = (e) => {
    // get current focus element
    const activeElement = document.activeElement
    // console.log("activeElement", activeElement)
    // if focus element is input or textarea, ignore keyboard
    if (
      activeElement?.tagName === "INPUT" ||
      activeElement?.tagName === "TEXTAREA"
    ) {
      return
    }
    if (e.keyCode === 32 && e.target === document.body) {
      e.preventDefault()
    }
    // get keys in keyboard template in one array
    const temp =
      customTemplate?.length > 0
        ? customTemplate
        : getKeyBoardTemplate(template, true, numericPadActive || useNumPad)
    const keys = temp.reduce((acc, row) => {
      return [...acc, ...row.map((key) => key)]
    }, [])
    if (useSpace) {
      keys.push(" ")
    }
    if (useEnter) {
      keys.push("Enter")
    }
    const { key } = e
    if (key === "Tab") {
      onTabKeyPress()
      return
    }
    // ignore space and return key
    if (
      disabled ||
      (key === " " && !useSpace) ||
      (key === "Enter" && !useEnter) ||
      keyDisabled.includes(key?.toLowerCase()) ||
      (key !== "Backspace" &&
        key !== "Enter" &&
        !keys?.includes(key?.toLowerCase()))
    ) {
      return
    }
    if (useBackspace && key === "Backspace")
      return onKeyPress(key?.toString()?.toLowerCase())
    if (useEnter && key === "Enter") {
      return onKeyPress(key?.toString()?.toLowerCase())
    }
    if (key.length === 1) {
      // send key letter as string
      onKeyPress(key.toString()?.toLowerCase())
    }
  }

  useEffect(() => {
    if (listenKeys) {
      window.addEventListener("keydown", handleUserKeyPress)
    }
    return () => {
      window.removeEventListener("keydown", handleUserKeyPress)
    }
  }, [
    onKeyPress,
    listenKeys,
    template,
    disabled,
    keyDisabled,
    useBackspace,
    numericPadActive,
    useSpace,
    useNumPad,
    customTemplate,
  ])

  const combinedSx = [
    (theme) => ({
      backgroundColor: theme.palette.secondary.main,
      // padding: 2,
      // boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
    }),
    ...(Array.isArray(props.sx) ? props.sx : props.sx ? [props.sx] : []),
  ]

  return (
    <Paper ref={paperRef} className="keyboard" sx={combinedSx}>
      <Stack
        spacing={0}
        direction={useOneLine ? "row" : "column"}
        sx={useOneLine ? styles.oneLine : null}
      >
        {keyboardTemplate.map((row, index) => {
          return (
            <Stack
              direction={"row"}
              key={index}
              className="keyboard-row"
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              // spacing={useOneLine ? 0 : 1}
              spacing={0}
              flexWrap={"wrap"}
              py={0}
            >
              {index === 0 &&
                keysBefore.map((letterObj) => {
                  return (
                    <Button
                      key={letterObj?.id}
                      variant="outlined"
                      className={getKeyThemeClass(keyTheme, letterObj?.id)}
                      sx={[styles.keyButton, keyStyle]}
                      onClick={() => letterObj.onKeyPress()}
                      disabled={disabled || keyDisabled.includes(letterObj?.id)}
                    >
                      {typeof letterObj?.letter === "string"
                        ? letterObj?.letter
                        : letterObj.letter}
                    </Button>
                  )
                })}
              {row.map((letter) => {
                return (
                  <Button
                    key={letter}
                    variant="outlined"
                    className={getKeyThemeClass(keyTheme, letter)}
                    sx={[styles.keyButton, keyStyle]}
                    onClick={() => onKeyPress(letter?.toLowerCase())}
                    disabled={disabled || keyDisabled.includes(letter)}
                  >
                    {letter}
                  </Button>
                )
              })}
            </Stack>
          )
        })}
      </Stack>
    </Paper>
  )
}

VirtualKeyboard.propTypes = {
  paperRef: PropsTypes.object,
  onKeyPress: PropsTypes.func,
  templateName: PropsTypes.string,
  templateId: PropsTypes.number,
  keyTheme: PropsTypes.array,
  keyDisabled: PropsTypes.array,
  disabled: PropsTypes.bool,
  fullKeyboard: PropsTypes.bool,
  keysBefore: PropsTypes.array,
  keysAfter: PropsTypes.array,
  keyStyle: PropsTypes.object,
  listenKeys: PropsTypes.bool,
  useOneLine: PropsTypes.bool,
  useBackspace: PropsTypes.bool,
  useSpace: PropsTypes.bool,
  useNumPad: PropsTypes.bool,
  numericPadActive: PropsTypes.bool,
  parentRef: PropsTypes.object,
  customTemplate: PropsTypes.array,
}
